import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  InputAdornment,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import html2canvas from "html2canvas";
import CircularProgress from "@mui/material/CircularProgress";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import MaleIcon from "@mui/icons-material/Male";
import FemaleIcon from "@mui/icons-material/Female";
import TransgenderIcon from "@mui/icons-material/Transgender";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import DescriptionIcon from "@mui/icons-material/Description";

import dayjs from "dayjs";

import { BASE_URL } from "../../constants";
import { appendSanitizedReportHtml } from "../../utils/reportSanitizer";

export default function DoctorPatientList() {
  const navigate = useNavigate();

  /* =========================================================
     STATE
  ========================================================= */

  const [patients, setPatients] = useState([]);

  const [stats, setStats] = useState({
    totalAppointments: 0,
    arrivedPatients: 0,
    completedPrescriptions: 0,
    pendingPrescriptions: 0,
  });

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [search, setSearch] = useState("");

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0],
  );

  const fetchPatients = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(
        `${BASE_URL}api/doctor/prescription/today-patients?date=${selectedDate}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Unable to load patients");
      }

      /* ===========================
           TABLE DATA
        =========================== */

      setPatients(data?.patients || []);

      /* ===========================
           DASHBOARD COUNTS
        =========================== */

      const arrivedPatientsCount =
        data?.patients?.filter(
          (patient) => patient.appointmentStatus === "arrived",
        ).length || 0;

      const completedPrescriptionsCount =
        data?.patients?.filter(
          (patient) => patient.appointmentStatus === "completed",
        ).length || 0;

      const pendingPrescriptionsCount =
        data?.patients?.filter(
          (patient) => patient.appointmentStatus === "pending",
        ).length || 0;

      setStats({
        totalAppointments: data?.patients?.length || 0,
        arrivedPatients: arrivedPatientsCount || 0,
        completedPrescriptions: completedPrescriptionsCount || 0,
        pendingPrescriptions: pendingPrescriptionsCount || 0,
      });
    } catch (requestError) {
      console.error("Get Patients Error:", requestError);

      setError(requestError.message || "Unable to load patients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, [selectedDate]);

  /* =========================================================
     SEARCH
  ========================================================= */

  const filteredPatients = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return patients;
    }

    return patients.filter((patient) =>
      [
        patient.patientId,
        patient.patientID,
        patient.name,
        patient.gender,
        patient.appointmentId,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [patients, search]);

  /* =========================================================
     PAGINATION
  ========================================================= */

  const paginatedPatients = useMemo(() => {
    const start = page * rowsPerPage;

    return filteredPatients.slice(start, start + rowsPerPage);
  }, [filteredPatients, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));

    setPage(0);
  };

  /* =========================================================
     SEARCH
  ========================================================= */

  const handleSearchChange = (event) => {
    setSearch(event.target.value);

    setPage(0);
  };

  /* =========================================================
     AGE
  ========================================================= */

  const showAge = (dob) => {
    const birthDate = dayjs(dob, "MMMM DD, YYYY");
    const today = dayjs();

    const years = today.diff(birthDate, "year");
    const months = today.diff(birthDate.add(years, "year"), "month");
    const days = today.diff(
      birthDate.add(years, "year").add(months, "month"),
      "day",
    );
    return `${years}Y ${months}M ${days}D`;
  };

  /* =========================================================
     GENDER ICON
  ========================================================= */

  const getGenderIcon = (gender = "") => {
    switch (gender.toLowerCase()) {
      case "male":
        return (
          <MaleIcon
            sx={{
              fontSize: ".85rem",
              color: "#2563eb",
            }}
          />
        );

      case "female":
        return (
          <FemaleIcon
            sx={{
              fontSize: ".85rem",
              color: "#db2777",
            }}
          />
        );

      default:
        return (
          <TransgenderIcon
            sx={{
              fontSize: ".85rem",
              color: "#7c3aed",
            }}
          />
        );
    }
  };

  /* =========================================================
     CREATE PRESCRIPTION
  ========================================================= */

  const handleCreatePrescription = (patient) => {
    navigate("/dashboard/prescription", {
      state: {
        selectedPatient: patient,
      },
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const monthAbbr = date.toLocaleString("en-US", { month: "short" });
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${monthAbbr}-${year}`;
  };

  const calculateAge = (dob) => {
    const birthDate = dayjs(dob, "MMMM DD, YYYY");
    const today = dayjs();

    const years = today.diff(birthDate, "year");
    const months = today.diff(birthDate.add(years, "year"), "month");
    const days = today.diff(
      birthDate.add(years, "year").add(months, "month"),
      "day",
    );
    return `${years}Y ${months}M ${days}D`;
  };

  const [pdfTemplate, setPdfTemplate] = useState("");
  const fetchPdfTemplate = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}api/user/common/get-pdfTemplate-appoint`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      const data = await response.json();
      setPdfTemplate(data.pdf.docAppointPdfUrl);
    } catch (error) {}
  };

  const fetchAppointment = async (appId) => {
    try {
      const response = await fetch(
        `${BASE_URL}api/doctor/prescription/appointment/${appId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      const data = await response.json();
      const app = data?.data;
      return app;
    } catch (error) {}
  };

  const [printingAppointmentId, setPrintingAppointmentId] = useState(null);
  const [printingPendingAppointmentId, setPrintingPendingAppointmentId] =
    useState(null);
  const [printRequest, setPrintRequest] = useState(null);

  const openPrintChoice = (patient, type) => {
    setPrintRequest({ patient, type });
  };

  const handlePrintChoice = (withTemplate) => {
    const request = printRequest;
    setPrintRequest(null);

    if (!request) return;

    if (request.type === "pending") {
      printPendingInvestigations(request.patient, withTemplate);
      return;
    }

    printDigitalPress(request.patient, withTemplate);
  };

  const printPendingInvestigations = async (patient, withTemplate = true) => {
    const appointmentId = patient?.appointmentMongoId;
    const prescriptionId = patient?.prescriptionId;

    if (!prescriptionId) {
      window.alert("No held prescription is available for this patient.");
      return;
    }

    setPrintingPendingAppointmentId(appointmentId);

    try {
      const response = await fetch(
        `${BASE_URL}api/doctor/prescription/draft/${prescriptionId}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );
      const data = await response.json();

      if (!response.ok || !data?.success || !data?.data) {
        throw new Error(data?.message || "Failed to load held prescription");
      }

      const investigations = Array.isArray(data.data.investigations)
        ? data.data.investigations
            .map((item) =>
              typeof item === "string"
                ? item.trim()
                : String(item?.testName || item?.name || "").trim(),
            )
            .filter(Boolean)
        : [];

      if (!investigations.length) {
        window.alert("No advised investigations are available to print.");
        return;
      }

      const patientAppointment = await fetchAppointment(appointmentId);

      if (!patientAppointment) {
        throw new Error("Failed to load appointment details");
      }

      const prescription = { ...data.data, investigations };

      if (withTemplate) {
        const templateUrl =
          patientAppointment?.doctorId?.whichAppointmentTemplate === "doctor"
            ? patientAppointment?.doctorId?.appointmentTemplate?.pdfUrl
            : pdfTemplate;

        if (!templateUrl) {
          throw new Error("Prescription print template is not available");
        }

        await generatePrescriptionPDF(
          patientAppointment,
          prescription,
          templateUrl,
          true,
        );
      } else {
        await generatePrescriptionPDF(
          patientAppointment,
          prescription,
          null,
          true,
        );
      }
    } catch (error) {
      console.error("Print pending investigations error:", error);
      window.alert(error.message || "Failed to print advised investigations.");
    } finally {
      setPrintingPendingAppointmentId(null);
    }
  };

  const printDigitalPress = async (appointment, withTemplate = true) => {
    const appointment_id = appointment?.appointmentMongoId;
    setPrintingAppointmentId(appointment_id);

    const patientAppointment = await fetchAppointment(appointment_id);
    try {
      const response = await fetch(
        `${BASE_URL}api/user/opd/prescription/${appointment_id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const res = await response.json();

      if (!res?.data || !patientAppointment) {
        throw new Error("Failed to load prescription details");
      }

      if (withTemplate) {
        const templateUrl =
          patientAppointment?.doctorId?.whichAppointmentTemplate === "doctor"
            ? patientAppointment?.doctorId?.appointmentTemplate?.pdfUrl
            : pdfTemplate;

        if (!templateUrl) {
          throw new Error("Prescription print template is not available");
        }

        await generatePrescriptionPDF(
          patientAppointment,
          res.data,
          templateUrl,
        );
      } else {
        await generatePrescriptionPDF(patientAppointment, res.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setPrintingAppointmentId(null);
    }
  };

  const generateDefaultPrescriptionPDF = async (
    appointment,
    prescription,
    investigationsOnly = false,
  ) => {
    const pdfDoc = await PDFDocument.create();
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const pageWidth = 595.28;
    const pageHeight = 841.89;
    const margin = 42;
    const theme = rgb(0.12, 0.24, 0.55);
    const muted = rgb(0.39, 0.45, 0.55);
    const line = rgb(0.86, 0.88, 0.92);
    let page;
    let y;

    const cleanItems = (items) =>
      (items || [])
        .map((item) =>
          typeof item === "string"
            ? item.trim()
            : String(
                item?.testName || item?.name || item?.testId?.testName || "",
              ).trim(),
        )
        .filter(Boolean);

    const addPage = () => {
      page = pdfDoc.addPage([pageWidth, pageHeight]);
      y = pageHeight - margin;
      page.drawText(
        investigationsOnly ? "ADVISED INVESTIGATIONS" : "PRESCRIPTION",
        {
          x: margin,
          y,
          size: 18,
          font: boldFont,
          color: theme,
        },
      );
      y -= 7;
      page.drawLine({
        start: { x: margin, y },
        end: { x: pageWidth - margin, y },
        thickness: 1.2,
        color: theme,
      });
      y -= 22;

      const patient = appointment?.patientId || {};
      const details = [
        `Patient: ${patient.name || "-"}`,
        `ID: ${patient.patientID || patient.patientId || "-"}`,
        `Age / Gender: ${patient.dob ? calculateAge(patient.dob) : "-"} / ${patient.gender || "-"}`,
        `Mobile: ${patient.mobile || "-"}`,
        `Visit: ${appointment?.appDate ? formatDate(appointment.appDate) : "-"}`,
        `Appointment ID: ${appointment?.appointmentId || "-"}`,
      ];

      details.forEach((value, index) => {
        const column = index % 2;
        const row = Math.floor(index / 2);
        page.drawText(value, {
          x: margin + column * 265,
          y: y - row * 15,
          size: 9.5,
          font: column === 0 ? boldFont : font,
          color: rgb(0.12, 0.16, 0.24),
        });
      });
      y -= 53;
      page.drawLine({
        start: { x: margin, y },
        end: { x: pageWidth - margin, y },
        thickness: 0.7,
        color: line,
      });
      y -= 18;
    };

    const ensureSpace = (required = 28) => {
      if (y - required < margin) addPage();
    };

    const drawSection = (title, items) => {
      const values = cleanItems(items);
      if (!values.length) return;
      ensureSpace(34);
      page.drawText(title.toUpperCase(), {
        x: margin,
        y,
        size: 11,
        font: boldFont,
        color: theme,
      });
      y -= 7;
      page.drawLine({
        start: { x: margin, y },
        end: { x: pageWidth - margin, y },
        thickness: 0.75,
        color: line,
      });
      y -= 14;
      values.forEach((item, index) => {
        ensureSpace(16);
        page.drawText(`${index + 1}. ${item}`, {
          x: margin + 4,
          y,
          size: 10,
          font,
          color: rgb(0.12, 0.16, 0.24),
          maxWidth: pageWidth - margin * 2 - 8,
        });
        y -= 15;
      });
      y -= 8;
    };

    addPage();
    const investigations = cleanItems(prescription?.investigations);

    if (investigationsOnly) {
      drawSection("Investigations Advised", investigations);
    } else {
      const vitals = prescription?.vitals || {};
      const vitalText = [
        vitals.height ? `Height: ${vitals.height} cm` : null,
        vitals.weight ? `Weight: ${vitals.weight} kg` : null,
        vitals.pulse ? `Pulse: ${vitals.pulse} /min` : null,
        vitals.bp ? `BP: ${vitals.bp}` : null,
      ].filter(Boolean);
      if (vitalText.length) drawSection("Vitals", [vitalText.join("   |   ")]);

      const plainNote = String(prescription?.note || "")
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      if (plainNote) drawSection("Clinical Note", [plainNote]);
      drawSection("Chief Complaints", prescription?.symptoms);
      drawSection("Investigations Advised", investigations);
      drawSection("Clinical Diagnosis", prescription?.diagnosis);

      const medicines = (prescription?.medicines || [])
        .filter((medicine) => medicine?.medicine)
        .map((medicine) => {
          const dosage = [
            medicine.morning || "0",
            medicine.afternoon || "0",
            medicine.evening || "0",
            medicine.night || "0",
          ].join("-");
          const duration = [medicine.duration, medicine.durationType]
            .filter(Boolean)
            .join(" ");
          return `${medicine.medicine}  |  ${dosage}${duration ? `  |  ${duration}` : ""}`;
        });
      drawSection("Medicines", medicines);
      drawSection("Advice", prescription?.advice || prescription?.advices);
      if (prescription?.nextVisitDate) {
        drawSection("Next Visit", [String(prescription.nextVisitDate)]);
      }
      if (prescription?.referredDoctor) {
        drawSection("Referred To", [prescription.referredDoctor]);
      }
    }

    const pdfBytes = await pdfDoc.save();
    const url = URL.createObjectURL(
      new Blob([pdfBytes], { type: "application/pdf" }),
    );
    window.open(url);
  };

  const generatePrescriptionPDF = async (
    appointment,
    prescription,
    templateUrl = null,
    investigationsOnly = false,
  ) => {
    const hasTemplate = Boolean(templateUrl);
    let templatePdf = null;
    let pdfDoc;

    if (hasTemplate) {
      const response = await fetch(templateUrl);
      if (!response.ok) {
        throw new Error("Unable to load prescription print template");
      }

      const existingPdfBytes = await response.arrayBuffer();
      templatePdf = await PDFDocument.load(existingPdfBytes);
      pdfDoc = await PDFDocument.load(existingPdfBytes);
    } else {
      pdfDoc = await PDFDocument.create();
      pdfDoc.addPage([595.28, 841.89]);
    }

    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

    let page = pdfDoc.getPages()[0];

    /* ================= THEME ================= */

    const THEME_COLOR = rgb(0.58, 0.212, 0.204);
    const MUTED_COLOR = rgb(0.45, 0.45, 0.45);
    const LINE_COLOR = rgb(0.85, 0.85, 0.85);

    /* ================= CONTENT AREA ================= */

    const pageHeight = page.getHeight();

    const appointmentTemplate =
      appointment?.doctorId?.appointmentTemplate || {};
    // The layout metadata applies to both modes. The only distinction is
    // whether the base page is the uploaded template or a blank A4 page.
    const template = appointmentTemplate;

    const showVitals = appointmentTemplate.showVitals === true;

    const prescriptionX = Number(template.prescriptionX || 30);
    const prescriptionY = Number(template.prescriptionY || 250);
    const prescriptionWidth = Number(template.prescriptionWidth || 530);
    const prescriptionHeight = Number(template.prescriptionHeight || 400);

    const CONTENT_START_X = prescriptionX;
    const CONTENT_START_Y = pageHeight - prescriptionY;

    const CONTENT_END_X = prescriptionX + prescriptionWidth;

    const CONTENT_END_Y = pageHeight - (prescriptionY + prescriptionHeight) + 8;

    const CONTENT_WIDTH = prescriptionWidth - 20;

    let y = CONTENT_START_Y - 10;
    let startX = CONTENT_START_X;

    /* ============================================================
     PAGINATION
  ============================================================ */

    const newPage = async () => {
      if (hasTemplate) {
        const [copiedPage] = await pdfDoc.copyPages(templatePdf, [0]);
        pdfDoc.addPage(copiedPage);
        page = copiedPage;
      } else {
        page = pdfDoc.addPage([595.28, 841.89]);
      }

      drawHeader();

      y = CONTENT_START_Y;
    };

    const ensureSpace = async (space = 80) => {
      if (y < CONTENT_END_Y) {
        await newPage();
      }
    };

    /* ============================================================
     DIVIDERS
  ============================================================ */

    const drawDivider = () => {
      y -= 8;

      page.drawLine({
        start: {
          x: startX,
          y,
        },
        end: {
          x: CONTENT_END_X,
          y,
        },
        thickness: 0.6,
        color: LINE_COLOR,
      });

      y -= 14;
    };

    const drawThinDivider = () => {
      y -= 5;

      page.drawLine({
        start: {
          x: startX,
          y,
        },
        end: {
          x: CONTENT_END_X,
          y,
        },
        thickness: 0.4,
        color: LINE_COLOR,
      });

      y -= 9;
    };

    /* ============================================================
     TEXT HELPERS
  ============================================================ */

    const wrapTextByWidth = (text, font, fontSize, maxWidth) => {
      const words = String(text).split(" ");

      const lines = [];

      let line = "";

      for (const word of words) {
        const testLine = line ? `${line} ${word}` : word;

        const width = font.widthOfTextAtSize(testLine, fontSize);

        if (width < maxWidth) {
          line = testLine;
        } else {
          if (line) {
            lines.push(line);
          }

          line = word;
        }
      }

      if (line) {
        lines.push(line);
      }

      return lines;
    };

    const cleanList = (items) =>
      (items || []).filter(
        (item) =>
          item !== null && item !== undefined && String(item).trim() !== "",
      );

    const stripHtml = (html) => (html || "").replace(/<[^>]*>/g, "").trim();

    /* ============================================================
     SECTION HEADER
  ============================================================ */

    const drawSectionHeader = (title, x = startX) => {
      page.drawText(title.toUpperCase(), {
        x,
        y,
        size: 11,
        font: boldFont,
        color: THEME_COLOR,
      });

      y -= 4;

      page.drawLine({
        start: {
          x,
          y,
        },
        end: {
          x: CONTENT_END_X,
          y,
        },
        thickness: 0.8,
        color: THEME_COLOR,
      });

      y -= 13;
    };

    /* ============================================================
     HEADER
  ============================================================ */

    const drawHeader = () => {
      const currentPageHeight = page.getHeight();

      let doctorInfoX;
      let doctorInfoY;

      let patientInfoLeftX;
      let patientInfoLeftY;

      let patientInfoRightX;
      let patientInfoRightY;

      let noteX;
      let noteY;

      let fontSizeD = 16;

      patientInfoLeftX = Number(template.patientInfoLeftX);
      patientInfoLeftY =
        currentPageHeight - Number(template.patientInfoLeftY) - 10;
      patientInfoRightX = Number(template.patientInfoRightX);
      patientInfoRightY =
        currentPageHeight - Number(template.patientInfoRightY) - 10;
      doctorInfoX = Number(template.doctorX);
      doctorInfoY = currentPageHeight - Number(template.doctorY) - 13;

      if (
        template.doctorInfo === "yes"
      ) {
        if (appointment?.doctorId?.name) {
          page.drawText(appointment.doctorId.name, {
            x: doctorInfoX,
            y: doctorInfoY,
            size: fontSizeD,
            font: boldFont,
            color: THEME_COLOR,
          });

          doctorInfoY -= 15;
        }

        (appointment?.doctorId?.qualifications || []).forEach((q) => {
          if (!q) return;

          page.drawText(q, {
            x: doctorInfoX,
            y: doctorInfoY,
            size: fontSizeD - 5,
            font: boldFont,
          });

          doctorInfoY -= 15;
        });
      }

      if (
        template.doctorNote === "yes" &&
        appointment?.doctorId?.note
      ) {
        page.drawText(appointment.doctorId.note, {
          x: noteX,
          y: noteY,
          size: fontSizeD - 7,
          font: boldFont,
        });
      }

      if (
        template.appNote === "yes" &&
        appointment?.note
      ) {
        page.drawText(appointment.note, {
          x: noteX,
          y: noteY - 15,
          size: fontSizeD - 7,
          font: boldFont,
        });
      }

      const fontSize = 11;

      page.drawText(appointment?.patientId?.name || "-", {
        x: patientInfoLeftX,
        y: patientInfoLeftY,
        size: fontSize + 1,
        font: boldFont,
      });

      if (appointment?.patientId?.patientID) {
        page.drawText("ID: " + appointment.patientId.patientID, {
          x: patientInfoRightX,
          y: patientInfoRightY,
          size: fontSize + 1,
          font: boldFont,
        });
      }

      patientInfoLeftY -= 15;
      patientInfoRightY -= 15;

      page.drawText(
        (appointment?.patientId?.gender || "-") +
          " / " +
          (calculateAge(appointment?.patientId?.dob) || "-"),
        {
          x: patientInfoLeftX,
          y: patientInfoLeftY,
          size: fontSize - 1,
          font,
        },
      );

      if (appointment?.appDate) {
        page.drawText("Visit: " + formatDate(appointment.appDate), {
          x: patientInfoRightX,
          y: patientInfoRightY,
          size: fontSize - 1,
          font,
        });
      }

      patientInfoLeftY -= 15;
      patientInfoRightY -= 15;

      if (appointment?.patientId?.mobile) {
        page.drawText("Mob: " + appointment.patientId.mobile, {
          x: patientInfoLeftX,
          y: patientInfoLeftY,
          size: fontSize - 1,
          font,
        });
      }

      if (appointment?.appointmentId) {
        page.drawText("Appt ID: " + appointment.appointmentId, {
          x: patientInfoRightX,
          y: patientInfoRightY,
          size: fontSize - 1,
          font,
        });
      }
    };

    drawHeader();

    y = CONTENT_START_Y;

    /* ============================================================
     1. VITALS
  ============================================================ */

    const drawVitalsLine = async () => {
      if (!showVitals) return;

      const v = prescription.vitals || {};

      const parts = [
        v.height ? `Height: ${v.height} cm` : null,

        v.weight ? `Weight: ${v.weight} kg` : null,

        v.pulse ? `Pulse: ${v.pulse} /min` : null,

        v.bp ? `BP: ${v.bp} mmHg` : null,
      ].filter(Boolean);

      if (parts.length === 0) {
        return;
      }

      await ensureSpace(20);

      page.drawText(parts.join("     |     "), {
        x: startX,
        y,
        size: 10.5,
        font,
        color: MUTED_COLOR,
      });

      y -= 10;

      drawThinDivider();
    };

    if (!investigationsOnly) {
      await drawVitalsLine();
    }

    /* ============================================================
     2. GENERIC TWO COLUMN SECTION
     
     Rules:
     
     BOTH:
     LEFT | RIGHT

     LEFT EMPTY:
     RIGHT moves to LEFT

     RIGHT EMPTY:
     LEFT remains LEFT

     BOTH EMPTY:
     skip entire section
  ============================================================ */

    const drawTwoColumnSection = async (
      leftTitle,
      leftItems,
      rightTitle,
      rightItems,
    ) => {
      let leftClean = cleanList(leftItems);
      let rightClean = cleanList(rightItems);

      /* Both empty → skip */
      if (leftClean.length === 0 && rightClean.length === 0) {
        return;
      }

      /* Left empty → move right to left */
      if (leftClean.length === 0 && rightClean.length > 0) {
        leftClean = rightClean;

        leftTitle = rightTitle;

        rightClean = [];
        rightTitle = "";
      }

      const colGap = 24;

      const colWidth = (CONTENT_WIDTH - colGap) / 2;

      const leftX = startX;

      const rightX = startX + colWidth + colGap;

      /* ========================================================
       Build wrapped lines
    ======================================================== */

      const buildLines = (items) => {
        const lines = [];

        items.forEach((item) => {
          const wrapped = wrapTextByWidth(
            `\u2022  ${item}`,
            font,
            10.5,
            colWidth - 8,
          );

          wrapped.forEach((line) => {
            lines.push(line);
          });
        });

        return lines;
      };

      const leftLines = buildLines(leftClean);

      const rightLines = buildLines(rightClean);

      const rowCount = Math.max(leftLines.length, rightLines.length);

      await ensureSpace(28 + rowCount * 13);

      /* ========================================================
       Headers
    ======================================================== */

      if (leftClean.length > 0) {
        page.drawText(leftTitle.toUpperCase(), {
          x: leftX,
          y,
          size: 10.5,
          font: boldFont,
          color: THEME_COLOR,
        });
      }

      if (rightClean.length > 0) {
        page.drawText(rightTitle.toUpperCase(), {
          x: rightX,
          y,
          size: 10.5,
          font: boldFont,
          color: THEME_COLOR,
        });
      }

      y -= 4;

      /* Header line */

      page.drawLine({
        start: {
          x: startX,
          y,
        },
        end: {
          x: CONTENT_END_X,
          y,
        },
        thickness: 0.8,
        color: THEME_COLOR,
      });

      y -= 13;

      const colTopY = y + 13;

      /* ========================================================
       Content
    ======================================================== */

      for (let i = 0; i < rowCount; i++) {
        if (leftLines[i]) {
          page.drawText(leftLines[i], {
            x: leftX + 2,
            y,
            size: 10.5,
            font,
          });
        }

        if (rightLines[i]) {
          page.drawText(rightLines[i], {
            x: rightX + 2,
            y,
            size: 10.5,
            font,
          });
        }

        y -= 13;
      }

      /* ========================================================
       Vertical divider only when both exist
    ======================================================== */

      if (leftClean.length > 0 && rightClean.length > 0) {
        page.drawLine({
          start: {
            x: leftX + colWidth + colGap / 2,
            y: colTopY,
          },
          end: {
            x: leftX + colWidth + colGap / 2,
            y: y + 13,
          },
          thickness: 0.5,
          color: LINE_COLOR,
        });
      }

      drawDivider();
    };

    /* ============================================================
     3. NOTES | CHIEF COMPLAINTS
  ============================================================ */

    const drawNotesAndComplaintsSection = async () => {
      const noteHtml = prescription.note;

      const hasNote = noteHtml && stripHtml(noteHtml) !== "";

      const complaints = cleanList(prescription.symptoms);

      /* Both empty → skip */
      if (!hasNote && complaints.length === 0) {
        return;
      }

      const colGap = 24;

      const colWidth = (CONTENT_WIDTH - colGap) / 2;

      const leftX = startX;

      const rightX = startX + colWidth + colGap;

      /*
       * Notes exists:
       *    Notes → left
       *    Complaints → right
       *
       * Notes doesn't exist:
       *    Complaints → left
       */

      const complaintsX = hasNote ? rightX : leftX;

      let noteCanvas = null;

      let noteTargetScale = 1;

      /* ========================================================
         Render rich text note
      ======================================================== */

      if (hasNote) {
        const RENDER_WIDTH_PX = 700;

        const tempDiv = document.createElement("div");

        tempDiv.id = "prescription-note-render";

        tempDiv.style.position = "absolute";

        tempDiv.style.top = "-9999px";

        tempDiv.style.left = "-9999px";

        tempDiv.style.width = `${RENDER_WIDTH_PX}px`;

        tempDiv.style.margin = "0";

        tempDiv.style.padding = "0";

        tempDiv.style.paddingBottom = "10px";

        tempDiv.style.border = "none";

        tempDiv.style.boxSizing = "border-box";

        tempDiv.style.background = "transparent";

        if (typeof appendSanitizedReportHtml === "function") {
          appendSanitizedReportHtml(tempDiv, noteHtml);
        } else {
          tempDiv.innerHTML = noteHtml;
        }

        const styleTag = document.createElement("style");

        styleTag.textContent = `
          #prescription-note-render {
            margin: 0;
            font-size: 30px !important;
            font-family: Arial, sans-serif;
            color: #1a1a1a;
            white-space: pre-wrap;
          }

          #prescription-note-render p,
          #prescription-note-render div {
            white-space: pre-wrap;
            margin: 0 0 6px 0;
            line-height: 26px !important;
          }

          #prescription-note-render .ql-align-center {
            text-align: center;
          }

          #prescription-note-render .ql-align-right {
            text-align: right;
          }

          #prescription-note-render .ql-align-left {
            text-align: left;
          }

          #prescription-note-render strong {
            font-weight: bold;
          }

          #prescription-note-render u {
            text-decoration: underline;
          }
        `;

        tempDiv.appendChild(styleTag);

        document.body.appendChild(tempDiv);

        noteCanvas = await html2canvas(tempDiv, {
          backgroundColor: null,
          scale: 3,
          scrollX: 0,
          scrollY: 0,
          useCORS: true,
        });

        document.body.removeChild(tempDiv);

        noteTargetScale = colWidth / noteCanvas.width;
      }

      /* ========================================================
         Chief complaints lines
      ======================================================== */

      const complaintLines = [];

      complaints.forEach((item) => {
        wrapTextByWidth(`\u2022  ${item}`, font, 10.5, colWidth - 8).forEach(
          (line) => {
            complaintLines.push(line);
          },
        );
      });

      await ensureSpace(60);

      /* ========================================================
         Headers
      ======================================================== */

      if (hasNote) {
        page.drawText("NOTES", {
          x: leftX,
          y,
          size: 10.5,
          font: boldFont,
          color: THEME_COLOR,
        });
      }

      if (complaintLines.length) {
        page.drawText("CHIEF COMPLAINTS", {
          x: complaintsX,
          y,
          size: 10.5,
          font: boldFont,
          color: THEME_COLOR,
        });
      }

      y -= 4;

      page.drawLine({
        start: {
          x: startX,
          y,
        },
        end: {
          x: CONTENT_END_X,
          y,
        },
        thickness: 0.8,
        color: THEME_COLOR,
      });

      y -= 13;

      const colTopY = y + 13;

      let leftY = y;
      let rightY = y;

      let notePaginated = false;

      /* ========================================================
         Chief Complaints
      ======================================================== */

      for (const line of complaintLines) {
        page.drawText(line, {
          x: complaintsX + 2,

          y: hasNote ? rightY : leftY,

          size: 10.5,
          font,
        });

        if (hasNote) {
          rightY -= 13;
        } else {
          leftY -= 13;
        }
      }

      /* ========================================================
         Notes
      ======================================================== */

      if (noteCanvas) {
        let startCanvasY = 0;

        const totalCanvasHeight = noteCanvas.height;

        while (startCanvasY < totalCanvasHeight) {
          const availableHeightPt = leftY - CONTENT_END_Y;

          const availableCanvasPx = Math.max(
            availableHeightPt / noteTargetScale,
            0,
          );

          if (availableCanvasPx <= 5) {
            notePaginated = true;

            await newPage();

            leftY = CONTENT_START_Y;

            continue;
          }

          const sliceCanvasHeight = Math.min(
            availableCanvasPx,
            totalCanvasHeight - startCanvasY,
          );

          const splitCanvas = document.createElement("canvas");

          splitCanvas.width = noteCanvas.width;

          splitCanvas.height = sliceCanvasHeight;

          const splitCtx = splitCanvas.getContext("2d");

          splitCtx.drawImage(
            noteCanvas,
            0,
            startCanvasY,
            noteCanvas.width,
            sliceCanvasHeight,
            0,
            0,
            noteCanvas.width,
            sliceCanvasHeight,
          );

          const splitImgData = splitCanvas.toDataURL("image/png");

          const splitPngImage = await pdfDoc.embedPng(splitImgData);

          const splitHeightPt = sliceCanvasHeight * noteTargetScale;

          page.drawImage(splitPngImage, {
            x: leftX,

            y: leftY - splitHeightPt,

            width: colWidth,

            height: splitHeightPt,
          });

          leftY -= splitHeightPt;

          startCanvasY += sliceCanvasHeight;

          if (startCanvasY < totalCanvasHeight) {
            notePaginated = true;

            await newPage();

            leftY = CONTENT_START_Y;
          }
        }
      }

      /* ========================================================
         Vertical divider
         
         Only when:
         - Notes exists
         - Complaints exist
         - Notes did not paginate
      ======================================================== */

      if (hasNote && complaintLines.length && !notePaginated) {
        const dividerBottom = Math.max(leftY, rightY, CONTENT_END_Y);

        page.drawLine({
          start: {
            x: leftX + colWidth + colGap / 2,
            y: colTopY,
          },

          end: {
            x: leftX + colWidth + colGap / 2,
            y: dividerBottom,
          },

          thickness: 0.5,

          color: LINE_COLOR,
        });
      }

      /* ========================================================
         Set Y according to whichever content is lower
      ======================================================== */

      y = Math.min(leftY, rightY);

      drawDivider();
    };

    if (!investigationsOnly) {
      await drawNotesAndComplaintsSection();
    }

    /* ============================================================
     4. INVESTIGATIONS | DIAGNOSIS
  ============================================================ */

    await drawTwoColumnSection(
      "Investigations Advised",

      (prescription.investigations || [])
        .map((test) =>
          typeof test === "string"
            ? test
            : test?.isCustom
              ? test?.testName
              : test?.testId?.testName,
        )
        .filter(Boolean),

      investigationsOnly ? "" : "Clinical Diagnosis",

      investigationsOnly ? [] : prescription.diagnosis,
    );

    /* ============================================================
     5. MEDICINES
  ============================================================ */

    const drawMedicinesTable = async () => {
      const medicines = (prescription.medicines || []).filter(
        (med) => med && med.medicine && String(med.medicine).trim(),
      );

      /* No valid medicines */
      if (medicines.length === 0) {
        return;
      }

      await ensureSpace(60);

      drawSectionHeader("Medicines");

      const colName = startX;

      const colDosage = startX + CONTENT_WIDTH * 0.56;

      const colDuration = startX + CONTENT_WIDTH * 0.8;

      /* Table headers */

      page.drawText("MEDICINE", {
        x: colName,
        y,
        size: 9,
        font: boldFont,
        color: MUTED_COLOR,
      });

      page.drawText("DOSAGE (M-A-E-N)", {
        x: colDosage,
        y,
        size: 9,
        font: boldFont,
        color: MUTED_COLOR,
      });

      page.drawText("DURATION", {
        x: colDuration,
        y,
        size: 9,
        font: boldFont,
        color: MUTED_COLOR,
      });

      y -= 5;

      page.drawLine({
        start: {
          x: startX,
          y,
        },
        end: {
          x: CONTENT_END_X,
          y,
        },
        thickness: 0.5,
        color: LINE_COLOR,
      });

      y -= 14;

      let index = 1;

      for (const med of medicines) {
        await ensureSpace(45);

        page.drawText(`${index}. ${med.medicine}`, {
          x: colName,
          y,
          size: 11,
          font: boldFont,
        });

        const dosage = [med.morning, med.afternoon, med.evening, med.night]
          .map((v) => v || "0")
          .join("-");

        page.drawText(dosage, {
          x: colDosage,
          y,
          size: 11,
          font,
        });

        const duration = [med.duration, med.durationType]
          .filter(Boolean)
          .join(" ");

        page.drawText(duration || "-", {
          x: colDuration,
          y,
          size: 11,
          font,
        });

        y -= 13;

        /* Medicine note */

        if (med.note) {
          const noteLines = wrapTextByWidth(
            med.note,
            font,
            9.5,
            CONTENT_WIDTH - 12,
          );

          for (const line of noteLines) {
            await ensureSpace();

            page.drawText(line, {
              x: colName + 12,
              y,
              size: 9.5,
              font,
              color: MUTED_COLOR,
            });

            y -= 11;
          }
        }

        page.drawLine({
          start: {
            x: startX,
            y: y + 4,
          },

          end: {
            x: CONTENT_END_X,
            y: y + 4,
          },

          thickness: 0.3,

          color: LINE_COLOR,
        });

        y -= 8;

        index++;
      }

      drawDivider();
    };

    if (!investigationsOnly) {
      await drawMedicinesTable();
    }

    /* ============================================================
     6. ADVICE | NEXT VISIT
  ============================================================ */

    if (!investigationsOnly) {
      await drawTwoColumnSection(
        "Advice",

        prescription.advice,

        "Next Visit",

        prescription.nextVisitDate
          ? [formatDate(prescription.nextVisitDate)]
          : [],
      );
    }

    /* ============================================================
     7. REFERRED TO DOCTOR
  ============================================================ */

    if (!investigationsOnly && prescription.referredDoctor) {
      await ensureSpace(20);

      page.drawText(`Referred To: ${prescription.referredDoctor}`, {
        x: startX,
        y,
        size: 10.5,
        font: boldFont,
        color: THEME_COLOR,
      });

      y -= 18;
    }

    /* ============================================================
     FOOTER
  ============================================================ */

    if (prescription.createdAt) {
      page.drawText(
        `Generated: ${new Date(prescription.createdAt).toLocaleString()}`,
        {
          x: startX,
          y: 30,
          size: 9,
          font,
          color: MUTED_COLOR,
        },
      );
    }

    /* ============================================================
     SAVE PDF
  ============================================================ */

    const pdfBytes = await pdfDoc.save();

    const blob = new Blob([pdfBytes], {
      type: "application/pdf",
    });

    const url = URL.createObjectURL(blob);

    window.open(url);
  };

  useEffect(() => {
    fetchPdfTemplate();
  }, []);

  /* =========================================================
     SUMMARY CARD
  ========================================================= */

  const SummaryCard = ({
    icon,
    value,
    label,
    iconBackground,
    iconColor,
    borderColor,
  }) => (
    <Box
      sx={{
        bgcolor: "#fff",

        border: `1px solid ${borderColor}`,

        borderRadius: "12px",

        px: 2,

        py: 1.6,

        display: "flex",

        alignItems: "center",

        gap: 1.3,

        minWidth: 0,

        boxShadow: "0 2px 8px rgba(15,23,42,.04)",

        transition: "all .18s ease",

        "&:hover": {
          transform: "translateY(-1px)",

          boxShadow: "0 5px 14px rgba(15,23,42,.07)",
        },
      }}
    >
      <Box
        sx={{
          width: 40,

          height: 40,

          borderRadius: "10px",

          bgcolor: iconBackground,

          color: iconColor,

          display: "flex",

          alignItems: "center",

          justifyContent: "center",

          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "1.25rem",

            fontWeight: 800,

            color: "#0f172a",

            lineHeight: 1.15,
          }}
        >
          {loading ? "—" : value}
        </Typography>

        <Typography
          sx={{
            fontSize: ".68rem",

            fontWeight: 600,

            color: "#64748b",

            mt: 0.25,

            whiteSpace: "nowrap",

            overflow: "hidden",

            textOverflow: "ellipsis",
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ mt: 1 }}>
      <Dialog
        open={Boolean(printRequest)}
        onClose={() => setPrintRequest(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            border: "1px solid #e2e8f0",
          },
        }}
      >
        <Box
          sx={{
            px: 2.25,
            py: 1.8,
            bgcolor: "#f8fafc",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Typography
            sx={{ fontSize: ".98rem", fontWeight: 800, color: "#0f172a" }}
          >
            Choose print layout
          </Typography>
          <Typography sx={{ mt: 0.35, fontSize: ".72rem", color: "#64748b" }}>
            {printRequest?.type === "pending"
              ? "Print advised investigations for the pending prescription."
              : "Print the completed prescription."}
          </Typography>
        </Box>

        <DialogContent sx={{ p: 2.25 }}>
          <Box sx={{ display: "grid", gap: 1.15 }}>
            <Button
              variant="contained"
              onClick={() => handlePrintChoice(true)}
              startIcon={<PrintRoundedIcon />}
              sx={{
                minHeight: 54,
                justifyContent: "flex-start",
                px: 2,
                textTransform: "none",
                borderRadius: "11px",
                fontWeight: 800,
                background: "linear-gradient(135deg,#1d4ed8,#4f46e5)",
              }}
            >
              Print with template
            </Button>
            <Typography
              sx={{ mt: -0.65, px: 1.25, fontSize: ".66rem", color: "#64748b" }}
            >
              Uses the organization or doctor prescription template exactly as
              before.
            </Typography>

            <Button
              variant="outlined"
              onClick={() => handlePrintChoice(false)}
              startIcon={<DescriptionIcon />}
              sx={{
                minHeight: 54,
                justifyContent: "flex-start",
                px: 2,
                textTransform: "none",
                borderRadius: "11px",
                fontWeight: 800,
                color: "#334155",
                borderColor: "#cbd5e1",
                "&:hover": { borderColor: "#64748b", bgcolor: "#f8fafc" },
              }}
            >
              Print without template
            </Button>
            <Typography
              sx={{ mt: -0.65, px: 1.25, fontSize: ".66rem", color: "#64748b" }}
            >
              Creates a clean default A4 PDF without any background template.
            </Typography>
          </Box>

          <Button
            fullWidth
            onClick={() => setPrintRequest(null)}
            sx={{
              mt: 1.5,
              textTransform: "none",
              color: "#64748b",
              fontWeight: 700,
            }}
          >
            Cancel
          </Button>
        </DialogContent>
      </Dialog>

      {/* =====================================================
          TODAY SUMMARY
      ===================================================== */}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(5, 1fr)",
          },
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: "14px",
            border: "1px solid #bbf7d0",
            bgcolor: "#f0fdf4",
            px: 2,
            py: 1.5,
            minHeight: 86,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            {/* Refresh Button */}
            <Tooltip title="Refresh / Sync latest appointments">
              <IconButton
                onClick={fetchPatients}
                size="small"
                sx={{
                  width: 40,
                  height: 40,
                  flexShrink: 0,

                  bgcolor: "#128C7E", // WhatsApp Green
                  color: "#fff",

                  border: "1px solid #0d6b5f",

                  transition: "all .25s ease",

                  "&:hover": {
                    bgcolor: "#0d6b5f",
                    transform: "rotate(180deg) scale(1.05)",
                    boxShadow: "0 6px 16px rgba(18,140,126,.30)",
                  },

                  "&:active": {
                    transform: "scale(.94)",
                  },
                }}
              >
                <RefreshRoundedIcon
                  sx={{
                    fontSize: "1rem",
                  }}
                />
              </IconButton>
            </Tooltip>

            {/* Date */}
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: ".72rem",
                  color: "#166534",
                  fontWeight: 700,
                  mb: 0.6,
                }}
              >
                Appointment Date
              </Typography>

              <TextField
                type="date"
                size="small"
                fullWidth
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 36,
                    borderRadius: "9px",
                    bgcolor: "#fff",

                    "& fieldset": {
                      borderColor: "#86efac",
                    },

                    "&:hover fieldset": {
                      borderColor: "#22c55e",
                    },

                    "&.Mui-focused fieldset": {
                      borderColor: "#16a34a",
                      borderWidth: "1px",
                    },

                    "&.Mui-focused": {
                      boxShadow: "0 0 0 3px rgba(34,197,94,.10)",
                    },
                  },

                  "& input": {
                    fontSize: ".78rem",
                    fontWeight: 600,
                    color: "#14532d",
                  },
                }}
              />
            </Box>
          </Box>
        </Paper>
        <SummaryCard
          value={stats.totalAppointments}
          label="Appointments"
          iconBackground="#eff6ff"
          iconColor="#2563eb"
          borderColor="#dbeafe"
          icon={
            <EventAvailableOutlinedIcon
              sx={{
                fontSize: "1.15rem",
              }}
            />
          }
        />

        <SummaryCard
          value={stats.arrivedPatients}
          label="Patients In Queue"
          iconBackground="#fefce8"
          iconColor="#ca8a04"
          borderColor="#fef08a"
          icon={
            <HowToRegOutlinedIcon
              sx={{
                fontSize: "1.15rem",
              }}
            />
          }
        />

        <SummaryCard
          value={stats.completedPrescriptions}
          label="Appointment Completed"
          iconBackground="#ecfdf5"
          iconColor="#059669"
          borderColor="#a7f3d0"
          icon={
            <MonitorHeartOutlinedIcon
              sx={{
                fontSize: "1.15rem",
              }}
            />
          }
        />

        <SummaryCard
          value={stats.pendingPrescriptions}
          label="Pending Prescriptions"
          iconBackground="#fff7ed"
          iconColor="#ea580c"
          borderColor="#fed7aa"
          icon={
            <PendingActionsOutlinedIcon
              sx={{
                fontSize: "1.15rem",
              }}
            />
          }
        />
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: "14px",

          border: "1px solid #e5e9ef",

          overflow: "hidden",

          boxShadow: "0 2px 12px rgba(10,31,68,0.06)",

          bgcolor: "#ffffff",
        }}
      >
        {/* ===================================================
            TOOLBAR
        =================================================== */}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: 1,
            flexWrap: "wrap",
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <TextField
            placeholder="Search patient..."
            variant="outlined"
            size="small"
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: "#94a3b8",

                      fontSize: "1rem",
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              width: {
                xs: "100%",
                sm: "250px",
              },

              "& .MuiOutlinedInput-root": {
                height: 38,

                borderRadius: "10px",

                bgcolor: "#fff",

                fontSize: ".82rem",

                "& fieldset": {
                  borderColor: "#e5e9ef",
                },

                "&:hover fieldset": {
                  borderColor: "#2a54a6",
                },

                "&.Mui-focused fieldset": {
                  borderColor: "#b93acf",

                  borderWidth: "1.5px",
                },
              },
            }}
          />

          <Box sx={{ flex: 1 }} />
          <TablePagination
            component="div"
            count={filteredPatients.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            // rowsPerPageOptions={[5, 10, 25]}
            rowsPerPageOptions={false}
            sx={{
              "& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel":
                {
                  fontSize: ".78rem",

                  color: "#64748b",
                },

              "& .MuiTablePagination-select": {
                fontSize: ".78rem",
              },

              "& .MuiIconButton-root": {
                color: "#2a54a6",
              },
            }}
          />
        </Box>

        {/* ===================================================
            ERROR
        =================================================== */}

        {error && (
          <Alert
            severity="error"
            sx={{
              borderRadius: 0,

              fontSize: ".75rem",

              borderBottom: "1px solid #fecaca",
            }}
          >
            {error}
          </Alert>
        )}

        {/* ===================================================
            TABLE
        =================================================== */}

        <Box sx={{ overflowX: "auto" }}>
          <Table
            sx={{
              minWidth: 700,
              bgcolor: "#fff",
            }}
          >
            {/* ==========================
                HEADER
            ========================== */}

            <TableHead>
              <TableRow
                sx={{
                  background:
                    "linear-gradient(135deg, rgba(42,84,166,0.04), rgba(185,58,207,0.03))",
                  "& .MuiTableCell-head": {
                    fontWeight: 700,
                    fontSize: ".7rem",
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: ".6px",
                    py: "10px",
                    px: "14px",
                    borderBottom: "1px solid #e5e9ef",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableCell>Appointment ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Age</TableCell>
                <TableCell align="center">Status / Action</TableCell>
              </TableRow>
            </TableHead>

            {/* ==========================
                LOADING
            ========================== */}

            {loading && (
              <TableBody>
                <TableRow>
                  <TableCell
                    colSpan={5}
                    sx={{
                      p: 0,
                      border: 0,
                    }}
                  >
                    <LinearProgress
                      sx={{
                        height: 2,
                        bgcolor: "rgba(185,58,207,.1)",
                        "& .MuiLinearProgress-bar": {
                          background: "linear-gradient(90deg,#2a54a6,#b93acf)",
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            )}

            {/* =================================================
                BODY
            ================================================= */}

            <TableBody>
              {!loading &&
                paginatedPatients.map((patient) => {
                  const isPrinting =
                    printingAppointmentId === patient.appointmentMongoId;
                  const isPrintingPending =
                    printingPendingAppointmentId === patient.appointmentMongoId;
                  return (
                    <TableRow
                      key={patient.appointmentMongoId || patient.appointmentId}
                      sx={{
                        transition: "background .2s ease",
                        "&:hover": {
                          bgcolor: "rgba(42,84,166,.025)",
                        },
                        "& .MuiTableCell-root": {
                          fontSize: ".82rem",
                          color: "#374151",
                          py: "10px",
                          px: "14px",
                          borderBottom: "1px solid #f1f5f9",
                        },
                        "&:last-child .MuiTableCell-root": {
                          borderBottom: 0,
                        },
                      }}
                    >
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            px: 1,
                            py: 0.4,
                            borderRadius: "6px",
                            bgcolor: "#eff6ff",
                            color: "#1d4ed8",
                            border: "1px solid #bfdbfe",
                            fontSize: ".7rem",
                            fontWeight: 700,
                          }}
                        >
                          {patient.appointmentId || "—"}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 31,
                              height: 31,
                              background:
                                "linear-gradient(135deg, rgba(42,84,166,.12), rgba(185,58,207,.12))",
                              color: "#2a54a6",
                              border: "1px solid rgba(42,84,166,.15)",
                              fontSize: ".7rem",
                              fontWeight: 800,
                            }}
                          >
                            {patient.name?.charAt(0)?.toUpperCase() || "P"}
                          </Avatar>
                          <Typography
                            sx={{
                              fontSize: ".8rem",
                              fontWeight: 600,
                              color: "#0a1f44",
                            }}
                          >
                            {patient.name || "—"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: "8px",
                            py: "2px",
                            borderRadius: "100px",
                            fontSize: ".7rem",
                            fontWeight: 600,
                            bgcolor:
                              patient.gender === "Male"
                                ? "rgba(42,84,166,.08)"
                                : patient.gender === "Female"
                                  ? "rgba(185,58,207,.08)"
                                  : "rgba(148,163,184,.15)",
                            color:
                              patient.gender === "Male"
                                ? "#2a54a6"
                                : patient.gender === "Female"
                                  ? "#b93acf"
                                  : "#64748b",
                            border:
                              patient.gender === "Male"
                                ? "1px solid rgba(42,84,166,.18)"
                                : patient.gender === "Female"
                                  ? "1px solid rgba(185,58,207,.18)"
                                  : "1px solid rgba(148,163,184,.25)",
                          }}
                        >
                          {getGenderIcon(patient.gender)}
                          {patient.gender || "—"}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#64748b !important",
                          fontWeight: "500 !important",
                        }}
                      >
                        {patient.age ? `${patient.age}Y` : showAge(patient.dob)}
                      </TableCell>
                      <TableCell align="center">
                        {patient?.appointmentStatus === "in_consult" ? (
                          <Button
                            type="button"
                            onClick={() => handleCreatePrescription(patient)}
                            startIcon={
                              <AssignmentOutlinedIcon
                                sx={{ fontSize: ".85rem !important" }}
                              />
                            }
                            endIcon={
                              <ArrowForwardRoundedIcon
                                sx={{ fontSize: ".85rem !important" }}
                              />
                            }
                            sx={{
                              height: 30,
                              px: 1.25,
                              borderRadius: "8px",
                              textTransform: "none",
                              whiteSpace: "nowrap",
                              fontSize: ".66rem",
                              fontWeight: 700,
                              color: "#2a54a6",
                              bgcolor: "rgba(42,84,166,.07)",
                              border: "1px solid rgba(42,84,166,.15)",
                              transition: "all .2s ease",
                              "&:hover": {
                                background:
                                  "linear-gradient(135deg, rgba(42,84,166,.15), rgba(185,58,207,.10))",
                                borderColor: "rgba(185,58,207,.25)",
                                transform: "translateY(-1px)",
                              },
                            }}
                          >
                            Prescription
                          </Button>
                        ) : (
                          <Box>
                            <Typography
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 1.25,
                                py: 0.45,
                                borderRadius: "999px",
                                fontSize: ".68rem",
                                fontWeight: 700,
                                textTransform: "capitalize",

                                bgcolor:
                                  patient?.appointmentStatus === "booked"
                                    ? "#f3f4f6"
                                    : patient?.appointmentStatus === "arrived"
                                      ? "#ecfdf5"
                                      : patient?.appointmentStatus === "pending"
                                        ? "#fefce8"
                                        : "#ede9fe",

                                color:
                                  patient?.appointmentStatus === "booked"
                                    ? "#475569"
                                    : patient?.appointmentStatus === "arrived"
                                      ? "#15803d"
                                      : patient?.appointmentStatus === "pending"
                                        ? "#a16207"
                                        : "#6d28d9",

                                border:
                                  patient?.appointmentStatus === "booked"
                                    ? "1px solid #d1d5db"
                                    : patient?.appointmentStatus === "arrived"
                                      ? "1px solid #bbf7d0"
                                      : patient?.appointmentStatus === "pending"
                                        ? "1px solid #fde68a"
                                        : "1px solid #ddd6fe",
                              }}
                            >
                              {{
                                booked: "Booked",
                                arrived: "Arrived",
                                pending: "Pending",
                                completed: "Completed",
                              }[patient?.appointmentStatus] || "-"}
                            </Typography>
                            {patient?.appointmentStatus === "completed" && (
                              <Tooltip
                                title={
                                  isPrinting
                                    ? "Generating PDF..."
                                    : "Print Prescription"
                                }
                                arrow
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      openPrintChoice(patient, "prescription")
                                    }
                                    disabled={isPrinting}
                                    sx={{
                                      width: 34,
                                      height: 34,
                                      ml: 1,
                                      borderRadius: "50%",

                                      bgcolor: "#eff6ff",
                                      color: "#2563eb",

                                      border: "1px solid #bfdbfe",

                                      transition: "all .2s ease",

                                      "&:hover": {
                                        bgcolor: "#dbeafe",
                                        color: "#1d4ed8",
                                        transform: isPrinting
                                          ? "none"
                                          : "translateY(-1px)",
                                        boxShadow: isPrinting
                                          ? "none"
                                          : "0 4px 10px rgba(37,99,235,.18)",
                                      },

                                      "&.Mui-disabled": {
                                        bgcolor: "#eff6ff",
                                        color: "#2563eb",
                                        opacity: 0.8,
                                      },

                                      "&:active": {
                                        transform: "scale(.96)",
                                      },
                                    }}
                                  >
                                    {isPrinting ? (
                                      <CircularProgress
                                        size={16}
                                        thickness={5}
                                        sx={{
                                          color: "#2563eb",
                                        }}
                                      />
                                    ) : (
                                      <PrintRoundedIcon
                                        sx={{
                                          fontSize: "1rem",
                                        }}
                                      />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {patient?.appointmentStatus === "pending" && (
                              <Tooltip
                                title={
                                  isPrintingPending
                                    ? "Preparing investigations..."
                                    : "Print advised investigations"
                                }
                                arrow
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      openPrintChoice(patient, "pending")
                                    }
                                    disabled={isPrintingPending}
                                    sx={{
                                      width: 34,
                                      height: 34,
                                      ml: 1,
                                      borderRadius: "50%",
                                      bgcolor: "#eff6ff",
                                      color: "#2563eb",
                                      border: "1px solid #bfdbfe",
                                      transition: "all .2s ease",
                                      "&:hover": {
                                        bgcolor: "#dbeafe",
                                        color: "#1d4ed8",
                                        transform: isPrintingPending
                                          ? "none"
                                          : "translateY(-1px)",
                                        boxShadow: isPrintingPending
                                          ? "none"
                                          : "0 4px 10px rgba(37,99,235,.18)",
                                      },
                                      "&.Mui-disabled": {
                                        bgcolor: "#eff6ff",
                                        color: "#2563eb",
                                        opacity: 0.8,
                                      },
                                      "&:active": { transform: "scale(.96)" },
                                    }}
                                  >
                                    {isPrintingPending ? (
                                      <CircularProgress
                                        size={16}
                                        thickness={5}
                                        sx={{ color: "#2563eb" }}
                                      />
                                    ) : (
                                      <PrintRoundedIcon
                                        sx={{ fontSize: "1rem" }}
                                      />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              {!loading && paginatedPatients.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align="center"
                    sx={{
                      py: 7,
                      border: 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.75,
                      }}
                    >
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: "50%",
                          background:
                            "linear-gradient(135deg, rgba(42,84,166,.08), rgba(185,58,207,.06))",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <PersonIcon
                          sx={{
                            color: "#94a3b8",
                            fontSize: "1.2rem",
                          }}
                        />
                      </Box>

                      <Typography
                        sx={{
                          fontSize: ".78rem",
                          fontWeight: 700,
                          color: "#64748b",
                        }}
                      >
                        {search
                          ? "No matching patients found"
                          : "No patients found for today"}
                      </Typography>
                      {search && (
                        <Typography
                          sx={{
                            fontSize: ".64rem",
                            color: "#94a3b8",
                          }}
                        >
                          Try searching with another patient name or ID.
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </TableContainer>
    </Box>
  );
}
